import React from 'react';
import { useMutation } from '@apollo/client';
import I18n from 'modules/i18n';
import cn from '@/utils/cn';
import Dropdown from '@/components/common/Dropdown';
import Icon from '@/components/ui/Icon';
import { SlideObject } from '@/components/admin/SlideShow/types';
import useSlideShowContext from '../../stores/useSlideShowContext';
import { UPDATE_IMAGE_MODEL_OBJECT_FIT } from '../../Utils/slideShowGraphQL';

const DEFAULT_OBJECT_FIT = 'contain';

const OPTIONS = [
  { labelKey: 'cover', value: 'cover' },
  { labelKey: 'contain', value: 'contain' },
  { labelKey: 'stretch', value: 'fill' },
];

type Props = {
  slideObject: SlideObject;
  styleDivRef: React.MutableRefObject<HTMLDivElement>;
};

const ChangeImageObjectFit = ({ slideObject, styleDivRef }: Props) => {
  const [updateImageModelMutation] = useMutation(UPDATE_IMAGE_MODEL_OBJECT_FIT);
  const updateSlideObject = useSlideShowContext(state => state.updateSlideObject);

  const { imageModel } = slideObject;

  if (!imageModel) return null;

  const currentValue = imageModel.styles?.object_fit || DEFAULT_OBJECT_FIT;
  const previewSrc = imageModel.image?.imageUrl;

  const applyPreview = (value: string) => {
    const img = styleDivRef.current?.querySelector<HTMLImageElement>('.slideObjectImageContainer img');
    if (img) img.style.objectFit = value;
  };

  const submitHandler = (objectFit: string) => {
    applyPreview(objectFit);

    updateImageModelMutation({ variables: { id: imageModel.id, objectFit } })
      .then((res) => {
        updateSlideObject({ ...slideObject, imageModel: res.data.updateImageModel.result.imageModel });
      })
      .catch((error) => { console.error(error); });
  };

  return (
    <Dropdown
      buttonClass="dropdown-item !tw-flex tw-w-full tw-items-center tw-pr-0 tw-cursor-pointer"
      dropdownPosition="right"
      toggleText={(
        <>
          {I18n.t('change_image_fit')}
          <Icon className="tw-ml-auto tw-mr-2" icon="ChevronRight" size={16} />
        </>
      )}
    >
      {OPTIONS.map(option => (
        <button
          key={option.value}
          className={cn(
            'tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-left tw-cursor-pointer',
            'tw-bg-inherit tw-border-none tw-font-normal tw-leading-normal tw-text-[#555]',
            'hover:tw-bg-[#eee] hover:tw-text-[#484848]',
          )}
          type="button"
          onClick={() => submitHandler(option.value)}
        >
          <span
            aria-hidden="true"
            className={cn(
              'tw-flex-shrink-0 tw-w-16 tw-h-9 tw-bg-[#e5e5e5] tw-overflow-hidden',
              'tw-border tw-border-[#ccc] tw-rounded-sm',
            )}
          >
            <img
              alt=""
              className="tw-w-full tw-h-full"
              src={previewSrc}
              style={{ objectFit: option.value as React.CSSProperties['objectFit'] }}
            />
          </span>
          <span className="tw-flex-grow">{I18n.t(option.labelKey)}</span>
          {option.value === currentValue && <i className="fa fa-check tw-text-tci-purple" />}
        </button>
      ))}
    </Dropdown>
  );
};

export default ChangeImageObjectFit;
